<template>
  <app-layout :state="state" header-type="landing">
    <template #header>
      <template v-if="state.user.is_monitor > 0">
        <icon-link
          :href="
            `${route('monitoring.index', {
              year: competitionYear
            })}#${competitionHash}`
          "
          icon="arrow-thin-left-circle"
          class="icons-white"
        >
          Back to monitoring
        </icon-link>
      </template>

      <h1>
        Project {{ state.project.ref }}
        <small class="font-normal text-xs opacity-50 leading-none block">{{
          state.competition.title
          }}</small>
      </h1>
    </template>
    <app-header>
      <template #navigation>
        <ul v-if="tabs">
          <li v-for="(tab, index) in tabs" :key="index">
            <a :href="route(tab.route, state.project.uuid)" :class="{
              'is-active': currentRoute === tab.route,
              'is-disabled': tab.disabled
            }">
              {{ tab.name }}
            </a>
          </li>
        </ul>
      </template>
      <template #tertiary>
        <portal-target name="tertiary" slim />
      </template>
    </app-header>
    <project-notices :notices="notices" />
    <slot />
  </app-layout>
</template>

<script>
import { userProjectMixin } from "../mixins/userProject.js";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import kebabCase from "lodash/kebabCase";
export default {
  name: "ProjectBase",
  mixins: [userProjectMixin],
  computed: {
    competitionYear() {
      return format(
        parseISO(this.state.competition.application_start_date),
        "yyyy"
      );
    },
    competitionHash() {
      return kebabCase(this.state.competition.title);
    },
    currentRoute() {
      return this.state.route;
    },
    tabs() {
      let tabsArray = [];

      // Overview
      tabsArray.push({
        name: "Overview",
        route: "projects.view"
      });

      // Team
      tabsArray.push({
        name: "Team",
        route: "projects.team.index"
      });

      // Requests
      if (this.state.user.is_monitor == 0) {
        tabsArray.push({
          name: "Requests",
          route: "projects.request.index"
        });
      }

      // Claims
      if (this.state.user.is_monitor == 0) {
        tabsArray.push({
          name: "Claims",
          route: "projects.claim.index"
        });
      }

      // Contracts
      tabsArray.push({
        name: "Contracts",
        route: "projects.contracts.index"
      });

      // Application
      tabsArray.push({
        name: "Application",
        route: "applications.view"
      });

      return tabsArray;
    },
    hasConditions() {
      return this.project.conditions &&
        !this.project.conditions_evidence_file_id
        ? true
        : false;
    },
    isTerminated() {
      return this.project.terminated_at !== null;
    },
    terminated_at() {
      return this.project.terminated_at;
    },
    notices() {
      const notices = [];

      if (this.isTerminated) {
        notices.push({
          type: "negative",
          label: "Project Terminated",
          content: `This project was terminated on ${this.formatDate(
            this.terminated_at
          )}. Access is limited to the overview section of this project only.`,
          action: {
            label: "Back To Dashboard",
            method: () => Turbolinks.visit(route("home"))
          }
        });
      } else {

        if (this.hasConditions && this.isLead && this.isEditor) {
          notices.push({
            type: "neutral",
            content:
              "<strong>WARNING:</strong> Your project has conditions which require verification. Please upload the relevant evidence to support this.",
            action: {
              label: "Upload",
              method: () =>
                Turbolinks.visit(
                  route("projects.contracts.index", this.project.uuid) +
                  "#conditions-card"
                )
            }
          });
        }

        if (this.contract && this.project.all_contracts_signed === 0) {
          notices.push({
            type: "neutral",
            content:
              "<strong>WARNING:</strong> Before this project can begin, all partners need to sign / accept changes to their contracts.",
            action: {
              label: "Sign",
              method: () =>
                Turbolinks.visit(
                  route("projects.contracts.index", this.project.uuid)
                )
            }
          });
        }

        if (this.state.forecast && !this.state.forecast.initials_completed && this.isLead && this.isEditor) {
          notices.push({
            type: "neutral",
            content:
              "<strong>WARNING:</strong> Before this project can begin, you need to submit your forecast for the project.",
            action: {
              label: "Submit Forecast",
              method: () => Turbolinks.visit(route("projects.view", this.project.uuid) + "#financial-forecast")
            }
          });
        }

      }

      return notices;
    }
  },
  methods: {
    formatDate(date) {
      return format(parseISO(date), "do MMMM yyyy");
    }
  }
};
</script>
